<!-- Single-line text form field with label, validation, and addon slot. -->

<script lang="ts">
import type { FormFieldController } from '@nzyme/vue-forms/components/defineFormField.js';
import { useTextInput } from '@nzyme/vue-forms/components/useTextInput.jsx';
import { vFocus } from '@nzyme/vue-utils/directives/vFocus.js';
import type { Slots } from '@nzyme/vue-utils/slots.js';

import { useTestHarness } from '@sigma/client/utils/useTestHarness.js';

import FormFieldWrapper from './FormFieldWrapper.vue';

export const TextInputProps = useTextInput.props;
export const TextInputEmits = useTextInput.emits;

export interface TextInputHarness {
  field: FormFieldController<string>;
  input: HTMLInputElement | null;
}
</script>

<script lang="ts" setup>
defineProps({
  ...TextInputProps,
  focus: Boolean,
});

defineEmits(TextInputEmits);
defineSlots<
  Slots<{
    addon: { field: FormFieldController<string>; inputId: string };
    info: { field: FormFieldController<string> };
  }>
>();

const { TextInput, field, input } = useTextInput();

useTestHarness<TextInputHarness>({
  field,
  input,
});
</script>

<template>
  <FormFieldWrapper
    :field="field"
    :label="label"
    :required="required"
    :disabled="disabled"
    :readonly="readonly"
  >
    <template #default="{ inputId }">
      <TextInput
        :id="inputId"
        v-focus="focus"
      />
    </template>

    <template #addon="{ inputId }">
      <slot
        name="addon"
        :field="field"
        :input-id="inputId"
      />
    </template>

    <template #info>
      <slot
        name="info"
        :field="field"
      />
    </template>
  </FormFieldWrapper>
</template>
